import type { Room } from 'colyseus.js';
import { getOnlineClient, resetOnlineClient } from '@/online/colyseusClient';
import { isDiscordActivity } from '@/utils/discordActivity';
import { ensureDiscordUser, getDiscordDisplayName, getDiscordInstanceId, getDiscordUserId } from '@/discord';
import { captureError } from '@/sentry';

export type InstanceLobbyStatus = 'none' | 'claimed' | 'live';
export type InstanceLobbyMode = 'coop' | 'telephone';

export type InstanceLobbyState = {
  status: InstanceLobbyStatus;
  hostSessionId: string;
  hostDiscordUserId: string;
  hostDiscordName: string;
  mode: InstanceLobbyMode | null;
  roomId: string | null;
  updatedAt: number;
};

export type InstanceLobbySnapshot = {
  connected: boolean;
  state: InstanceLobbyState;
  room: Room | null;
  error: string | null;
};

export type InstanceLobbyClaimOutcome = 'claimed' | 'denied' | 'unavailable';

const listeners = new Set<(snapshot: InstanceLobbySnapshot) => void>();

let instanceLobbyRoom: Room | null = null;
const DEFAULT_STATE: InstanceLobbyState = {
  status: 'none',
  hostSessionId: '',
  hostDiscordUserId: '',
  hostDiscordName: '',
  mode: null,
  roomId: null,
  updatedAt: 0,
};

let instanceLobbyState: InstanceLobbyState = { ...DEFAULT_STATE };
let instanceLobbyError: string | null = null;
let connectPromise: Promise<Room | null> | null = null;
let stateDisposer: (() => void) | null = null;
let leaveDisposer: ((code: number, reason?: string) => void) | null = null;
let pendingClaim: Promise<InstanceLobbyClaimOutcome> | null = null;

export function getInstanceLobbyState(): InstanceLobbyState {
  return instanceLobbyState;
}

export function getInstanceLobbyRoom(): Room | null {
  return instanceLobbyRoom;
}

export function isInstanceLobbyHost(): boolean {
  if (!instanceLobbyRoom) return false;
  return instanceLobbyState.hostSessionId === instanceLobbyRoom.sessionId;
}

export function subscribeInstanceLobby(listener: (snapshot: InstanceLobbySnapshot) => void): () => void {
  listeners.add(listener);
  listener(getSnapshot());
  return () => listeners.delete(listener);
}

export async function ensureInstanceLobbyRoom(): Promise<Room | null> {
  if (!isDiscordActivity()) return null;
  if (instanceLobbyRoom) return instanceLobbyRoom;
  if (connectPromise) return connectPromise;

  const instanceId = getDiscordInstanceId();
  if (!instanceId) {
    instanceLobbyError = 'missing_instance_id';
    notify();
    return null;
  }

  connectPromise = (async () => {
    try {
      const client = getOnlineClient();
      const room = await client.joinOrCreate('instance_lobby', { instanceId });
      attachRoom(room);
      return room;
    } catch (err) {
      captureError(err, { scope: 'instanceLobby', action: 'ensureInstanceLobbyRoom' });
      instanceLobbyError = err instanceof Error ? err.message : String(err);
      resetOnlineClient();
      notify();
      return null;
    } finally {
      connectPromise = null;
    }
  })();

  return connectPromise;
}

export async function claimInstanceLobby(params: { mode: InstanceLobbyMode }): Promise<InstanceLobbyClaimOutcome> {
  if (!isDiscordActivity()) return 'unavailable';
  if (pendingClaim) return pendingClaim;

  pendingClaim = (async () => {
    const room = await ensureInstanceLobbyRoom();
    if (!room) return 'unavailable';

    const user = await ensureDiscordUser();
    const hostDiscordName = user?.globalName || user?.username || getDiscordDisplayName() || '';
    const hostDiscordUserId = user?.id || getDiscordUserId() || '';

    const result = await waitForResult(room, 'claim', {
      mode: params.mode,
      hostDiscordName,
      hostDiscordUserId,
    });

    return result === 'ok' ? 'claimed' : result === 'denied' ? 'denied' : 'unavailable';
  })();

  try {
    return await pendingClaim;
  } finally {
    pendingClaim = null;
  }
}

export async function publishInstanceLobby(params: { mode: InstanceLobbyMode; roomId: string }): Promise<void> {
  if (!isDiscordActivity()) return;
  const room = await ensureInstanceLobbyRoom();
  if (!room) return;

  const hostDiscordName = getDiscordDisplayName() || '';
  const hostDiscordUserId = getDiscordUserId() || '';

  room.send('publish', {
    mode: params.mode,
    roomId: params.roomId,
    hostDiscordName,
    hostDiscordUserId,
  });
}

export async function releaseInstanceLobby(): Promise<void> {
  if (!isDiscordActivity()) return;
  const room = await ensureInstanceLobbyRoom();
  if (!room) return;
  room.send('release', {});
}

function attachRoom(room: Room): void {
  detachRoom();
  instanceLobbyRoom = room;
  instanceLobbyError = null;
  syncState(room.state);

  const onState = (state: unknown) => syncState(state);
  room.onStateChange(onState);
  stateDisposer = () => room.onStateChange.remove(onState);

  const onLeave = () => {
    detachRoom();
    notify();
  };
  room.onLeave(onLeave);
  leaveDisposer = onLeave;

  notify();
}

function detachRoom(): void {
  if (instanceLobbyRoom && stateDisposer) {
    stateDisposer();
  }
  if (instanceLobbyRoom && leaveDisposer) {
    instanceLobbyRoom.onLeave.remove(leaveDisposer);
  }
  stateDisposer = null;
  leaveDisposer = null;
  instanceLobbyRoom = null;
  instanceLobbyState = { ...DEFAULT_STATE };
}

function syncState(state: unknown): void {
  const parsed = state && typeof state === 'object' ? (state as Record<string, unknown>) : {};
  const statusRaw = parsed.status;
  const status: InstanceLobbyStatus = statusRaw === 'claimed' || statusRaw === 'live' ? statusRaw : 'none';
  const modeRaw = parsed.mode;
  const mode: InstanceLobbyMode | null = modeRaw === 'coop' || modeRaw === 'telephone' ? modeRaw : null;
  const roomIdRaw = typeof parsed.roomId === 'string' ? parsed.roomId.trim() : '';
  instanceLobbyState = {
    status,
    hostSessionId: typeof parsed.hostSessionId === 'string' ? parsed.hostSessionId : '',
    hostDiscordUserId: typeof parsed.hostDiscordUserId === 'string' ? parsed.hostDiscordUserId : '',
    hostDiscordName: typeof parsed.hostDiscordName === 'string' ? parsed.hostDiscordName : '',
    mode,
    roomId: roomIdRaw || null,
    updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : 0,
  };
  notify();
}

function notify(): void {
  const snapshot = getSnapshot();
  listeners.forEach((listener) => listener(snapshot));
}

function getSnapshot(): InstanceLobbySnapshot {
  return {
    connected: Boolean(instanceLobbyRoom),
    state: instanceLobbyState,
    room: instanceLobbyRoom,
    error: instanceLobbyError,
  };
}

async function waitForResult(
  room: Room,
  action: 'claim' | 'publish' | 'release',
  payload: Record<string, unknown>,
): Promise<'ok' | 'denied' | 'unavailable'> {
  return new Promise((resolve) => {
    let settled = false;
    const timeout = window.setTimeout(() => {
      cleanup();
      resolve('unavailable');
    }, 2000);

    const cleanup = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      offOk();
      offDenied();
    };

    const offOk = room.onMessage(`${action}_result`, (msg: unknown) => {
      const parsed = msg as { ok?: boolean };
      cleanup();
      resolve(parsed?.ok ? 'ok' : 'denied');
    });

    const offDenied = room.onMessage(`${action}_denied`, () => {
      cleanup();
      resolve('denied');
    });

    room.send(action, payload);
  });
}
